import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import api from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import { LEGAL_PAGES } from "../data/legalContent";
import { Printer, List } from "lucide-react";

const ORIGIN = "https://medicalpeptides.ai";

/**
 * Renders a legal page. Hybrid content resolution:
 *   1) Try `GET /api/content/legal/{slug}` — admin-editable via CMS
 *   2) Fall back to hardcoded content from `legalContent.js`
 *
 * The CMS document (when present) can override:
 *   title, seo_title, meta_description, body_html, last_updated_on,
 *   and (optional) `extra.toc` = [{id, label}]
 *
 * TOC anchor ids must match `id` attributes inside body_html <h2> tags.
 */
export default function LegalPage({ slug }) {
  const fallback = LEGAL_PAGES[slug];
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/content/legal/${slug}`)
      .then(({ data }) => { if (!cancelled) setRemote(data); })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [slug]);

  // Merge: remote overrides individual fields; fallback fills gaps.
  const page = useMemo(() => {
    if (!fallback && !remote) return null;
    const r = remote || {};
    const f = fallback || {};
    return {
      title: r.title || f.title,
      seo_title: r.seo_title || f.seo_title,
      meta_description: r.meta_description || f.meta_description,
      body_html: r.body_html || f.body_html,
      last_updated_on: r.last_updated_on || f.last_updated_on,
      toc: (r.extra && Array.isArray(r.extra.toc) && r.extra.toc.length ? r.extra.toc : f.toc) || [],
    };
  }, [remote, fallback]);

  const sanitizedBody = useMemo(
    () => (page?.body_html
      ? DOMPurify.sanitize(page.body_html, {
          USE_PROFILES: { html: true },
          FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input"],
          FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
        })
      : ""),
    [page?.body_html]
  );

  if (!page) {
    return (
      <div className="vv-container py-24 text-center">
        <h1 className="font-display text-3xl">Page not found</h1>
        <Link to="/" className="vv-btn-primary mt-6 inline-flex">Return to home</Link>
      </div>
    );
  }

  const canonical = `${ORIGIN}/${slug}`;
  const fullTitle = page.seo_title || `${page.title} | Medical Peptides Powered by VidaVital Medical`;

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.meta_description,
    url: canonical,
    dateModified: page.last_updated_on || undefined,
    publisher: {
      "@type": "MedicalOrganization",
      name: "Medical Peptides Powered by VidaVital Medical",
      url: ORIGIN,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Legal", item: `${ORIGIN}/${slug}` },
      { "@type": "ListItem", position: 3, name: page.title, item: canonical },
    ],
  };

  return (
    <div data-testid={`legal-page-${slug}`} className="legal-page">
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={page.meta_description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Medical Peptides Powered by VidaVital Medical" />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={page.meta_description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://medicalpeptides.ai/social-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={page.meta_description} />
        <meta name="twitter:image" content="https://medicalpeptides.ai/social-preview.jpg" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <section className="vv-hero print:bg-white">
        <div className="vv-container py-12">
          <Breadcrumbs items={[{ name: page.title, path: `/${slug}` }]} />
          <div className="vv-hero-eyebrow mb-2 print:hidden">Legal</div>
          <h1 className="font-display text-4xl md:text-5xl" data-testid="legal-title">{page.title}</h1>
          <p className="mt-3 text-sm text-stone-500" data-testid="legal-last-updated">
            Last Updated: <time dateTime={page.last_updated_on}>{page.last_updated_on}</time>
          </p>
        </div>
      </section>

      <section className="vv-container py-10 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Table of contents — desktop sidebar */}
        {page.toc?.length ? (
          <aside className="hidden lg:block print:hidden" data-testid="legal-toc-desktop">
            <div className="sticky top-24">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
                <List size={14} /> Table of Contents
              </div>
              <nav>
                <ol className="space-y-2 text-sm">
                  {page.toc.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="text-stone-600 hover:text-[color:var(--vv-gold-deep)] transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="legal-print-btn"
                className="mt-6 inline-flex items-center gap-2 text-xs text-stone-500 hover:text-[color:var(--vv-gold-deep)]"
              >
                <Printer size={14} /> Print this page
              </button>
            </div>
          </aside>
        ) : null}

        <div className="min-w-0">
          {/* Table of contents — mobile collapsible */}
          {page.toc?.length ? (
            <details className="lg:hidden vv-card p-5 mb-6 print:hidden" data-testid="legal-toc-mobile">
              <summary className="cursor-pointer text-sm font-medium text-stone-800 flex items-center gap-2">
                <List size={14} /> Table of Contents
              </summary>
              <ol className="mt-3 space-y-2 text-sm">
                {page.toc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="text-stone-600 hover:text-[color:var(--vv-gold-deep)]">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </details>
          ) : null}

          <article
            className="vv-card p-6 md:p-8 print:shadow-none print:border-0 print:p-0 scroll-smooth"
            data-testid="legal-body"
            dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          />

          <div className="mt-8 text-xs text-stone-500 border-t border-stone-100 pt-4 flex flex-wrap gap-4 items-center print:hidden">
            <span>Last Updated: {page.last_updated_on}</span>
            <Link to="/privacy-policy" className="hover:text-stone-800">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-stone-800">Terms of Use</Link>
            <Link to="/telehealth-consent" className="hover:text-stone-800">Telehealth Consent</Link>
            <Link to="/medical-disclaimer" className="hover:text-stone-800">Medical Disclaimer</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
